import { estados_admins } from "./utils";

const SIN_DATO = 'Sin dato';

const normalizarValorGrupo = (value) => {
  if (value === null || value === undefined || value === '') return SIN_DATO;
  return String(value).trim() || SIN_DATO;
};

const fechaInscripcionKey = (value) => {
  if (!value) return SIN_DATO;
  return String(value).split(/[T ]/)[0] || SIN_DATO;
};

const textoSinTildes = (value) => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const tipoCobroKey = (item) => {
  const tipoPlanId = Number(item.tipo_plan_id);
  if ([1, 6, 11].includes(tipoPlanId)) return 'Credito';
  if ([2, 7, 12].includes(tipoPlanId)) return 'Contado';

  if (item.tipo_plan_id === null || item.tipo_plan_id === undefined || item.tipo_plan_id === '') {
    const planCobro = textoSinTildes(item.plan_cobro);
    if (planCobro.includes('credito')) return 'Credito';
    if (planCobro.includes('contado')) return 'Contado';
  }

  return 'otros';
};

const contarPorCampo = (items, getKey) => {
  const map = new Map();

  (items || []).forEach((item) => {
    const nombre = normalizarValorGrupo(getKey(item));
    const actual = map.get(nombre) || { nombre, total: 0 };
    actual.total += 1;
    map.set(nombre, actual);
  });

  return Array.from(map.values()).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return a.nombre.localeCompare(b.nombre);
  });
};

const contarPorClave = (items, getGroup) => {
  const map = new Map();

  (items || []).forEach((item) => {
    const group = getGroup(item);
    const clave = normalizarValorGrupo(group.clave);
    const nombre = normalizarValorGrupo(group.nombre);
    const actual = map.get(clave) || { nombre, total: 0 };
    actual.total += 1;
    map.set(clave, actual);
  });

  return Array.from(map.values()).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return a.nombre.localeCompare(b.nombre);
  });
};

const estadoProgramaKey = (item) => {
  const padreId = item.estado_programa_padre_id;
  const hijoId = item.estado_programa_id;
  const padre = normalizarValorGrupo(item.estado_programa_padre);
  const hijo = normalizarValorGrupo(item.estado_programa);

  return {
    clave: `${padreId || padre}-${hijoId || hijo}`,
    nombre: `${padre} - ${hijo}`,
  };
};

const ordenarTiposCobro = (items) => {
  const prioridad = {
    Credito: 1,
    Contado: 2,
    otros: 3,
  };

  return items.sort((a, b) => {
    const prioridadA = prioridad[a.nombre] || 99;
    const prioridadB = prioridad[b.nombre] || 99;
    if (prioridadA !== prioridadB) return prioridadA - prioridadB;
    if (b.total !== a.total) return b.total - a.total;
    return a.nombre.localeCompare(b.nombre);
  });
};

export const resumenInscripcionesGerencia = (items) => {
  const estados = estados_admins()
  return {
    categorias: contarPorCampo(items, (item) => item.categoria),
    unidades: contarPorCampo(items, (item) => item.unidad),
    convenios: contarPorCampo(items, (item) => item.convenio),
    sedes: contarPorCampo(items, (item) => item.sede_nombre),
    asesores: contarPorCampo(items, (item) => item.asesor_nombre),
    areas: contarPorCampo(items, (item) => item.area),
    estadosAdministrativos: contarPorCampo(items, (item) => {
      if (item.estado_administrativo_id === null || item.estado_administrativo_id === undefined) return 'Sin estado';
      return `${estados.find(e=>e.id == item.estado_administrativo_id)?.text}s`;
    }),
    estadosIns: contarPorCampo(items, (item) => {
      if (item.estado_ins === 1) return 'Inscritos';
      if (item.estado_ins === 0) return 'Pre inscritos';
      return item.estado_ins != null ? `Otros (${item.estado_ins})` : 'Sin estado';
    }),
    programas: contarPorCampo(items, (item) => item.programa_nombre),
    estadosPrograma: contarPorClave(items, estadoProgramaKey),
    tiposCobro: ordenarTiposCobro(contarPorCampo(items, tipoCobroKey)),
    tiposInscripcion: contarPorCampo(items, (item) => item.tipo_inscripcion),
    fechasInscripcion: contarPorCampo(items, (item) => fechaInscripcionKey(item.fecha_inscripcion)),
  };
};

export const cardsInscripcionesGerencia = (items, opciones = {}) => {
  const resumen = resumenInscripcionesGerencia(items);

  const cards = [
    { titulo: 'Por sede', icono: 'mdi-domain', color: 'teal', items: resumen.sedes },
    { titulo: 'Por categoria', icono: 'mdi-shape-outline', color: 'indigo', items: resumen.categorias },
    { titulo: 'Por area', icono: 'mdi-view-grid-outline', color: 'deep-purple', items: resumen.areas },
    { titulo: 'Por unidad', icono: 'mdi-office-building-outline', color: 'cyan', items: resumen.unidades },
    { titulo: 'Por convenio', icono: 'mdi-handshake-outline', color: 'pink', items: resumen.convenios },
    { titulo: 'Por asesor', icono: 'mdi-account-tie', color: 'blue', items: resumen.asesores },
    { titulo: 'Estado administrativo', icono: 'mdi-clipboard-check-outline', color: 'orange', items: resumen.estadosAdministrativos },
  ];

  if (opciones.mostrarEstadoIns) {
    cards.push({ titulo: 'Estado de inscripción', icono: 'mdi-account-check-outline', color: 'deep-orange', items: resumen.estadosIns });
  }

  cards.push(
    { titulo: 'Estado programa', icono: 'mdi-progress-check', color: 'amber', items: resumen.estadosPrograma },
    { titulo: 'Credito / contado', icono: 'mdi-cash-multiple', color: 'lime', items: resumen.tiposCobro },
    { titulo: 'Por tipo de inscripcion', icono: 'mdi-card-account-details-outline', color: 'purple', items: resumen.tiposInscripcion },
    { titulo: 'Por programa', icono: 'mdi-school-outline', color: 'green', items: resumen.programas },
    { titulo: 'Fecha inscripcion', icono: 'mdi-calendar-month-outline', color: 'blue-grey', items: resumen.fechasInscripcion }
  );

  return cards;
};
